import { Component, ChangeDetectorRef, isDevMode, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { CentreService } from "../../../../services/centre.service";
import {Observable, Subject} from "rxjs";
import {filter, take, takeUntil} from "rxjs/operators";
import { Centre } from "../../../../interfaces/Centres";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog } from "@angular/material/dialog";
import {Teacher} from "../../../../components/dashboard/mis-education/mis-education.component";
import {ChildService} from "../../../../components/child/child.service";
import {EducationService} from "../../../education/education.service";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {LoadingService} from "../../../../services/loading.service";
import {MatTableDataSource} from "@angular/material/table";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {EmployeeService} from "../../../../services/employee.service";
import {ActivatedRoute, Router} from "@angular/router";
import {PermissionType} from "../../../../components/employee/PermissionType.enum";
import firebase from "firebase/compat/app";
import {DatePipe} from "@angular/common";
import {Timestamp} from "@angular/fire/firestore";

interface ComponentToggle {
  id: string;
  label: string;
  visible: boolean;
}

@Component({
  selector: 'app-education-presentation',
  templateUrl: './education-presentation.component.html',
  styleUrls: ['./education-presentation.component.scss'],
})
export class EducationPresentationComponent implements OnInit, OnDestroy {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  mStartDate: Date | null = null;
  mEndDate: Date | null = null;
  centres$: Observable<Centre[]>;
  /** Selected centre codes – empty array means All Centres */
  selectedCentres: string[] = [];
  /** Cached centre list for code→name lookups */
  loadedCentres: Centre[] = [];
  mCentre?: Centre;
  dataSource = new MatTableDataSource<Teacher>([]);
  teacherColumns: string[] = [
    'no',
    'photo',
    'userName',
    'centre',
    'individualSessionCount',
    'groupSessionCount',
    'followUpCount',
    'totalSessionCount'
  ];
  educationSessions: any[] = [];
  groupSessions: any[] = [];
  followUpSessions: any[] = [];
  allUsers: Teacher[] = [];
  /** Snapshot of the employee-list teachers — never modified after loadUsers() */
  private baseUsers: Teacher[] = [];
  destroyed$ = new Subject<void>();

  // Highlights table
  highlightsDataSource = new MatTableDataSource<any>([]);
  isHighlightsLoading = false;
  highlightsPageIndex = 0;
  highlightsPageSize = 10;
  readonly highlightsPageSizeOptions = [5, 10, 20, 50];

  get pagedHighlights(): any[] {
    const start = this.highlightsPageIndex * this.highlightsPageSize;
    return this.highlightsDataSource.data.slice(start, start + this.highlightsPageSize);
  }

  onHighlightsPageChange(event: any): void {
    this.highlightsPageIndex = event.pageIndex;
    this.highlightsPageSize = event.pageSize;
  }

  // Teacher map for O(1) lookups
  private teacherMap = new Map<string, Teacher>();

  /**
   * Name alias map: maps variant/alternate names → canonical (primary) name.
   * All keys and values must be lowercase trimmed.
   */
  private readonly nameAliases: { [alias: string]: string } = {
    'ankita ajay shelar': 'ankita shelar',
    // Add more aliases here as needed
  };

  /** Normalize a teacher name: lowercase, trim, and resolve aliases */
  private normalizeName(name: string): string {
    const key = (name || '').toLowerCase().trim();
    return this.nameAliases[key] || key;
  }

  // Role priority for sorting
  rolePriority: any = {
    'lead teacher': 1,
    'sr teacher': 2,
    'teacher': 3,
  };
  // Component visibility toggles
  components: ComponentToggle[] = [
    { id: 'teachers-structure', label: 'Teachers Structure', visible: true },
    { id: 'education-session-counts-new', label: 'Education Sessions Report (Children 14+)', visible: true },
    { id: 'learning-concepts', label: 'Learning New Concepts', visible: true },
    { id: 'teaching-plan-14-18', label: 'Teaching Plan (14-18 Years)', visible: true },
    { id: 'education-analytical-report', label: 'Education Analytical Report', visible: true },
    { id: 'teacher-session-counts', label: 'Teacher Session Counts (Centre Wise)', visible: true },
    { id: 'mis-report', label: 'MIS Report', visible: true },
    { id: 'gallery', label: 'Gallery', visible: true },
    { id: 'remarks', label: 'Remarks', visible: true }
  ];

  /** True when every section is visible. Computed fresh on every check — never drifts out of sync. */
  get allSelected(): boolean {
    return this.components.every(c => c.visible);
  }

  /** True when SOME (but not all) sections are visible — drives the indeterminate state on the checkbox. */
  get someSelected(): boolean {
    return this.components.some(c => c.visible) && !this.allSelected;
  }

  // Gallery images
  galleryImages: any[] = [];

  // Search control
  searchPressed: boolean = false;
  searchTrigger: number = 0; // Counter to trigger child component searches

  // Learning Concepts data
  learningConceptOptions: string[] = [];   // fetched from types/Learning Concepts
  selectedLearningConcepts: string[] = []; // multi-select bound value
  goodTouchBadTouch: string = '';
  /** Display fn for plain-string options in searchable-select */
  displayConcept = (c: any) => c;
  displayCentre = (c: Centre) => c?.centreName ?? '';
  get selectedCentreObjects(): Centre[] {
    return this.loadedCentres.filter(c => this.selectedCentres.includes(c.centreCode));
  }
  onCentreSelectionChange(centres: Centre[]): void {
    this.selectedCentres = (centres ?? []).map(c => c.centreCode);
  }

  // Teaching Plan 14-18 Years data
  teachingPlanParents: string = '';
  teachingPlanChildren: string = '';

  // Remarks data
  remarks: string = '';

  // Edit mode tracking
  isEditMode: boolean = false;
  currentPresentationId: string | null = null;

  /** True when viewing a saved presentation in read-only mode (no ?mode=edit) */
  get isViewMode(): boolean {
    return this.currentPresentationId !== null && !this.isEditMode;
  }

  /** Dynamic page title based on current mode */
  get pageTitle(): string {
    if (this.isViewMode) return 'View Presentation';
    if (this.isEditMode) return 'Edit Presentation';
    return 'New Education Presentation';
  }

  // Expose PermissionType to template
  readonly PermissionType = PermissionType;

  constructor(
    private centreService: CentreService,
    private snackBar: MatSnackBar,
    private centerService: CentreService,
    private childService: ChildService,
    private educationService: EducationService,
    private mDatabase: AngularFireDatabase,
    private readonly mEmployeeService:EmployeeService,
    public loadingService: LoadingService,
    private firestore: AngularFirestore,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef
  ) {
    this.centres$ = this.centreService.getAllCentres();
    // Cache centres for code→name lookups
    this.centreService.getAllCentres().pipe(takeUntil(this.destroyed$)).subscribe(c => this.loadedCentres = c);
    // Fetch learning concept options from Types
    this.mDatabase.list<string>('types/Learning Concepts').valueChanges()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(opts => this.learningConceptOptions = opts as string[]);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    // Debug: Verify Gallery is in components array
    console.log('📊 Education Presentation - Components loaded:', this.components.length, 'components');
    console.log('📸 Gallery component:', this.components.find(c => c.id === 'gallery'));

    this.loadUsers();
    this.checkForLoadPresentation();
  }

  checkForLoadPresentation(): void {
    let lastLoadedId: string | null = null;

    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(params => {
      const id = params['id'];
      const mode = params['mode'];

      // Always sync the mode flag (safe — doesn't discard data)
      this.isEditMode = mode === 'edit';

      if (id) {
        this.currentPresentationId = id;

        // Only fetch from Firestore when the ID actually changes (prevents
        // re-loading and discarding unsaved changes when switchToEditMode()
        // updates the URL with mode=edit)
        if (id !== lastLoadedId) {
          lastLoadedId = id;
          this.loadPresentation(id);
        }
      }
    });
  }

  loadPresentation(id: string): void {
    this.loadingService.setLoading(true);
    this.firestore.collection(this.env.educationPresentationList)
      .doc(id)
      .get()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (doc) => {
          if (doc.exists) {
            const data: any = doc.data();

            // Load filters — support both old (string) and new (string[]) saved format
            if (Array.isArray(data.selectedCentres)) {
              this.selectedCentres = data.selectedCentres;
            } else if (data.selectedCentre) {
              this.selectedCentres = [data.selectedCentre];  // migrate old single-centre saves
            } else {
              this.selectedCentres = [];
            }
            this.mStartDate = data.startDate?.toDate() || null;
            this.mEndDate = data.endDate?.toDate() || null;

            // Load component visibility
            if (data.componentVisibility) {
              data.componentVisibility.forEach((cv: any) => {
                const component = this.components.find(c => c.id === cv.id);
                if (component) {
                  component.visible = cv.visible;
                }
              });

              // Migrate: old presentations stored a single 'myspace-project-overview' entry.
              // If it was hidden, propagate that state to the three new standalone sections.
              const oldMyspace = data.componentVisibility.find((cv: any) => cv.id === 'myspace-project-overview');
              if (oldMyspace && !oldMyspace.visible) {
                ['education-analytical-report', 'teacher-session-counts', 'mis-report'].forEach(id => {
                  const c = this.components.find(comp => comp.id === id);
                  if (c) c.visible = false;
                });
              }

              this.updateAllSelectedState();
            }

            // Load form data
            this.selectedLearningConcepts = data.selectedLearningConcepts || [];
            this.goodTouchBadTouch = data.goodTouchBadTouch || '';
            this.teachingPlanParents = data.teachingPlanParents || '';
            this.teachingPlanChildren = data.teachingPlanChildren || '';
            this.remarks = data.remarks || '';

            // Load gallery images
            this.galleryImages = data.galleryImages || [];

            // Set search pressed to show content
            this.searchPressed = true;
            this.searchTrigger++;

            this.loadingService.setLoading(false);

            if (this.isEditMode) {
              this.snackBar.open('Presentation loaded for editing', 'Close', { duration: 3000 });
            } else {
              this.snackBar.open('Presentation loaded', 'Close', { duration: 3000 });
            }
          } else {
            this.loadingService.setLoading(false);
            this.snackBar.open('Presentation not found', 'Close', { duration: 3000 });
          }
        },
        (error) => {
          this.loadingService.setLoading(false);
          this.snackBar.open('Error loading presentation: ' + error.message, 'Close', { duration: 5000 });
          console.error('Error loading presentation:', error);
        }
      );
  }

  loadUsers() {
    this.loadingService.setLoading(true);

    this.mEmployeeService.getTeachers().pipe(
      filter((res: any) => Array.isArray(res) && res.length > 0),
      take(1),
      takeUntil(this.destroyed$)
    ).subscribe({
      next: (res: any) => {
        if (!res || !Array.isArray(res)) {
          this.loadingService.setLoading(false);
          return;
        }

        const teachers: Teacher[] = res
          .map((r: any): Teacher | null => {
            const u = r;
            if (!u) return null;
            if (u.disabled === true) return null;
            if (u.jobtitle?.toLowerCase() === 'it') return null;

            const role = (u.jobtitle || '').toLowerCase();

            let centres: string[] = [];
            if (u.centre) {
              if (Array.isArray(u.centre)) {
                centres = u.centre;
              } else if (typeof u.centre === 'string') {
                centres = [u.centre];
              } else {
                centres = Object.values(u.centre) as string[];
              }
            }

            if (centres.length === 0) return null;

            // Pan India logic: Lead/Sr Teacher stay as Pan India; others drop it
            if (centres.includes('Pan India')) {
              if (role === 'lead teacher' || role === 'sr teacher') {
                centres = ['Pan India'];
              } else {
                centres = centres.filter(c => c !== 'Pan India');
              }
            }

            return {
              profileImages: u.profileImages || 'assets/img/user-avatar.png',
              userName: u.userName || 'Unknown',
              designation: u.jobtitle || 'Teacher',
              dateOfJoining: u.dateOfJoining || 'N/A',
              centre: centres,
              individualSessionCount: 0,
              groupSessionCount: 0,
              followUpCount: 0,
              sessionObserve: 0
            } as Teacher;
          })
          .filter((t: Teacher | null): t is Teacher => t !== null && t.centre.length > 0);

        this.allUsers = teachers;
        this.baseUsers = [...teachers]; // ✅ Save clean snapshot for re-use on each Search()
        this.setupTeacherMap();
        this.applyRoleSort();
        this.dataSource.data = [...teachers];
        this.loadingService.setLoading(false);
        console.log('✅ Teachers loaded:', teachers.length);
      },
      error: (err) => {
        console.error('❌ Error loading teachers:', err);
        this.loadingService.setLoading(false);
      }
    });
  }

  /** Setup teacher map for O(1) lookups */
  private setupTeacherMap(): void {
    this.teacherMap.clear();
    this.allUsers.forEach(teacher => {
      if (teacher.userName) {
        this.teacherMap.set(this.normalizeName(teacher.userName), teacher);
      }
    });
  }

  /** Sort teachers by role hierarchy, then alphabetically */
  applyRoleSort(): void {
    this.allUsers.sort((a: Teacher, b: Teacher) => {
      const roleA = a.designation?.toLowerCase() || '';
      const roleB = b.designation?.toLowerCase() || '';
      const priorityA = this.rolePriority[roleA] || 99;
      const priorityB = this.rolePriority[roleB] || 99;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return a.userName.localeCompare(b.userName);
    });
    this.dataSource.data = [...this.allUsers];
  }

  /**
   * Fetch isHighlight=true sessions (Individual + Follow-Up) filtered by
   * selectedCentres and date range, and populate highlightsDataSource.
   */
  async loadHighlights(): Promise<void> {
    if (!this.mStartDate || !this.mEndDate) return;

    this.isHighlightsLoading = true;

    // Run one pair of queries per centre (or a single 'All' query when none selected)
    const centreCodes = this.selectedCentres.length > 0 ? this.selectedCentres : ['All'];

    try {
      const promises = centreCodes.flatMap(code => [
        this.educationService.individualHighlights(code, this.mStartDate!, this.mEndDate!, 'Present', null, null),
        this.educationService.getFollowUpHighlights(code, this.mStartDate!, this.mEndDate!, null)
      ]);

      const results = await Promise.all(promises);

      let allData: any[] = [];

      // Results come in pairs: [ind_0, fu_0, ind_1, fu_1, ...]
      for (let i = 0; i < results.length; i += 2) {
        const indRes: any = results[i];
        const fuRes: any = results[i + 1];

        const indData = indRes.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data() as any,
          _source: 'Individual'
        }));

        const fuData = fuRes.docs.map((doc: any) => {
          const d = doc.data() as any;
          return {
            id: doc.id,
            ...d,
            sessionDate: d.date,   // normalise date → sessionDate
            _source: 'Follow-Up'
          };
        });

        allData = [...allData, ...indData, ...fuData];
      }

      // De-duplicate by id (multiple centres can return same doc)
      const seen = new Set<string>();
      allData = allData.filter(item => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });

      // Sort by sessionDate descending
      allData.sort((a, b) => {
        const dateA = a.sessionDate?.toDate?.() ?? new Date(0);
        const dateB = b.sessionDate?.toDate?.() ?? new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      this.highlightsDataSource.data = allData;
      this.highlightsPageIndex = 0;
      console.log(`✅ Highlights loaded: ${allData.length} records`);
    } catch (err) {
      console.error('❌ Error loading highlights:', err);
    } finally {
      this.isHighlightsLoading = false;
    }
  }

  /** Format a Firestore Timestamp or Date as dd-MMM-yyyy */
  getHighlightDate(date: any): string {
    if (!date) return '-';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return this.datePipe.transform(d, 'dd-MMM-yyyy') || '-';
  }

  /** Format a Firestore Timestamp or Date as dd-MMM-yyyy h:mm a */
  getHighlightDatetime(date: any): string {
    if (!date) return '-';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return this.datePipe.transform(d, 'dd-MMM-yyyy h:mm a') || '-';
  }

  /** Returns 'P' for Present, 'A' for Absent */
  getPresent(element: any): string {
    return element.present === 'Present' ? 'P' : 'A';
  }

  get totalIndividual(): number {
    return this.dataSource.data.reduce((sum, t) => sum + (t.individualSessionCount || 0), 0);
  }

  get totalGroup(): number {
    return this.dataSource.data.reduce((sum, t) => sum + (t.groupSessionCount || 0), 0);
  }

  get totalFollowUp(): number {
    return this.dataSource.data.reduce((sum, t) => sum + (t.followUpCount || 0), 0);
  }
  getPhoto(img?: string): string {
    return img && img.trim() !== '' ? img : 'assets/images/userbig.png';
  }

  onImageError(event: any) {
    event.target.src = 'assets/images/userbig.png';
  }


  OnCentreChange(event: any): void {
    const centreCode = event.value;

    // Handle "All Centres" selection
    if (centreCode === 'All Centres') {
      this.mCentre = undefined;
      console.log('Selected: All Centres');
      return;
    }

    this.centreService.getAllCentres().pipe(takeUntil(this.destroyed$)).subscribe((centres: Centre[]) => {
      this.mCentre = centres.find((c: Centre) => c.centreCode === centreCode);
      console.log('Selected Centre:', this.mCentre);
    });
  }

  async Search(): Promise<void> {
    if (!this.mStartDate || !this.mEndDate) {
      this.snackBar.open('Please select a date range', 'Close', { duration: 3000 });
      return;
    }

    this.loadingService.setLoading(true);

    // ✅ Restore clean employee list (no synthetic carry-overs from previous Search runs)
    this.allUsers = this.baseUsers.map(t => ({ ...t, individualSessionCount: 0, groupSessionCount: 0, followUpCount: 0, sessionObserve: 0 }));

    try {
      // Fetch pre-aggregated summary docs filtered by centre & date range
      const summaryDocs = await this.educationService.getSummaryForDateRange(
        this.selectedCentres, this.mStartDate, this.mEndDate
      );

      console.log(`📊 Summary docs received for teacher table: ${summaryDocs.length}`);

      // Aggregate by normalized teacher name (handles multi-month / multi-centre docs)
      const teacherSumMap = new Map<string, { individual: number; group: number; followUp: number; originalName: string }>();
      summaryDocs.forEach((doc: any) => {
        const key = this.normalizeName(doc.teacherName);
        if (!key) return;
        const existing = teacherSumMap.get(key) || { individual: 0, group: 0, followUp: 0, originalName: doc.teacherName };
        teacherSumMap.set(key, {
          individual: existing.individual + (doc.individualCount || 0),
          group: existing.group + (doc.groupCount || 0),
          followUp: existing.followUp + (doc.followUpCount || 0),
          originalName: existing.originalName || doc.teacherName
        });
      });

      console.log(`📊 Aggregated summary for ${teacherSumMap.size} unique teachers`);

      // Build centre-codes map for synthetic teachers (avoids fetching sessions)
      const teacherCentresMap = new Map<string, Set<string>>();
      summaryDocs.forEach((doc: any) => {
        if (!doc.teacherName || !doc.centreCode) return;
        const key = this.normalizeName(doc.teacherName);
        if (!teacherCentresMap.has(key)) teacherCentresMap.set(key, new Set<string>());
        teacherCentresMap.get(key)!.add(doc.centreCode);
      });

      // ─── Add synthetic rows for teachers in summary but not in employee list ───
      const excludedNames = ['master admin', 'na', 'developercode', 'admin'];
      for (const [key, counts] of teacherSumMap) {
        if (excludedNames.includes(key)) continue;
        const exists = this.allUsers.some(u => this.normalizeName(u.userName) === key);
        if (!exists) {
          console.log(`➕ Synthetic teacher row: ${counts.originalName}`);
          const centreCodes = Array.from(teacherCentresMap.get(key) || new Set<string>());
          const allCentres = await this.centreService.getAllCentres().pipe(take(1)).toPromise();
          const centreNames = centreCodes.map(code => {
            const found = allCentres?.find((c: Centre) => c.centreCode === code);
            return found?.centreName || code;
          });
          this.allUsers.push({
            profileImages: 'assets/images/userbig.png',
            userName: counts.originalName || key,
            designation: 'Teacher',
            centre: centreNames.length > 0 ? centreNames : ['—'],
            disabled: false,
            individualSessionCount: 0,
            groupSessionCount: 0,
            followUpCount: 0,
            sessionObserve: 0,
            isSynthetic: true
          } as Teacher);
        }
      }

      // Remove excluded names
      this.allUsers = this.allUsers.filter(u => !excludedNames.includes(this.normalizeName(u.userName)));

      // Sort real employees before synthetics, then deduplicate by normalized name
      this.allUsers.sort((a, b) => (a.isSynthetic ? 1 : 0) - (b.isSynthetic ? 1 : 0));
      const seenNames = new Set<string>();
      this.allUsers = this.allUsers.filter(u => {
        const key = this.normalizeName(u.userName);
        if (seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      });

      // Rebuild teacher map after allUsers was modified
      this.setupTeacherMap();

      // Apply aggregated counts back to teacher objects
      this.allUsers.forEach(teacher => {
        const key = this.normalizeName(teacher.userName);
        const counts = teacherSumMap.get(key);
        if (counts) {
          teacher.individualSessionCount = counts.individual;
          teacher.groupSessionCount = counts.group;
          teacher.followUpCount = counts.followUp;
        } else {
          teacher.individualSessionCount = 0;
          teacher.groupSessionCount = 0;
          teacher.followUpCount = 0;
        }
      });

      // Show only teachers who have sessions in the date range
      // (consistent whether centres are selected or not)
      const filteredForTable = this.selectedCentres.length > 0
        ? this.allUsers.filter(t => teacherSumMap.has(this.normalizeName(t.userName)))
        : this.allUsers.filter(t => teacherSumMap.has(this.normalizeName(t.userName)));

      // Apply role sort
      filteredForTable.sort((a, b) => {
        const priorityA = this.rolePriority[a.designation?.toLowerCase() || ''] || 99;
        const priorityB = this.rolePriority[b.designation?.toLowerCase() || ''] || 99;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return a.userName.localeCompare(b.userName);
      });

      this.dataSource.data = [...filteredForTable];
      console.log(`✅ Teacher table updated: ${filteredForTable.length} teachers (${filteredForTable.filter((t: any) => t.isSynthetic).length} synthetic)`);
    } catch (error) {
      console.error('❌ Error loading teacher summary for presentation:', error);
    }

    this.searchPressed = true;
    this.searchTrigger++;

    // Load highlights table for the selected period
    await this.loadHighlights();

    this.loadingService.setLoading(false);
    this.cdr.detectChanges(); // ✅ Force Angular to pick up changes from async operations
    this.snackBar.open('Loading data...', 'Close', { duration: 2000 });
  }

  Clear(): void {
    this.selectedCentres = [];
    this.mStartDate = null;
    this.mEndDate = null;
    this.searchPressed = false;

    // Reset teacher counts and restore full list
    this.allUsers.forEach(u => {
      u.individualSessionCount = 0;
      u.groupSessionCount = 0;
      u.followUpCount = 0;
    });
    this.dataSource.data = [...this.allUsers];

    // Reset highlights
    this.highlightsDataSource.data = [];

    // Reset all form content fields
    this.selectedLearningConcepts = [];
    this.goodTouchBadTouch = '';
    this.teachingPlanParents = '';
    this.teachingPlanChildren = '';
    this.remarks = '';
    this.galleryImages = [];

    // Reset component visibility to all-visible defaults
    this.components.forEach(c => c.visible = true);

    // Reset edit/view state
    this.currentPresentationId = null;
    this.isEditMode = false;

    this.snackBar.open('Cleared', 'Close', { duration: 2000 });
  }

  Save(): void {
    if (!this.mStartDate || !this.mEndDate) {
      this.snackBar.open('Please select a date range before saving', 'Close', { duration: 3000 });
      return;
    }

    this.loadingService.setLoading(true);

    const presentationData = {
      // Filters — store as array (new format); also keep legacy field for old readers
      selectedCentres: this.selectedCentres,
      selectedCentre: this.selectedCentres[0] || '',
      centreName: this.getCentreName(),
      startDate: firebase.firestore.Timestamp.fromDate(this.mStartDate),
      endDate: firebase.firestore.Timestamp.fromDate(this.mEndDate),

      componentVisibility: this.components.map(c => ({
        id: c.id,
        label: c.label,
        visible: c.visible
      })),

      goodTouchBadTouch: this.goodTouchBadTouch,
      selectedLearningConcepts: this.selectedLearningConcepts,
      teachingPlanParents: this.teachingPlanParents,
      teachingPlanChildren: this.teachingPlanChildren,
      remarks: this.remarks,
      galleryImages: this.galleryImages || [],
      lastModified: firebase.firestore.Timestamp.now()
    };

    if (this.isEditMode && this.currentPresentationId) {
      this.firestore.collection(this.env.educationPresentationList)
        .doc(this.currentPresentationId)
        .update(presentationData)
        .then(() => {
          this.loadingService.setLoading(false);
          this.snackBar.open(`Presentation updated successfully!`, 'Close', { duration: 5000 });
        })
        .catch(error => {
          this.loadingService.setLoading(false);
          this.snackBar.open('Error updating presentation: ' + error.message, 'Close', { duration: 5000 });
          console.error('Error updating presentation:', error);
        });
    } else {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      this.firestore.collection(this.env.educationPresentationList)
        .add({
          ...presentationData,
          createdAt: firebase.firestore.Timestamp.now(),
          createdBy: userData?.userName || 'Unknown'
        })
        .then(docRef => {
          this.loadingService.setLoading(false);
          this.snackBar.open(`Presentation saved! ID: ${docRef.id}`, 'Close', { duration: 5000 });
          this.currentPresentationId = docRef.id;
          this.isEditMode = true;
        })
        .catch(error => {
          this.loadingService.setLoading(false);
          this.snackBar.open('Error saving: ' + error.message, 'Close', { duration: 5000 });
          console.error('Error saving presentation:', error);
        });
    }
  }

  /** Returns comma-joined centre names; falls back to 'All Centres' when nothing selected. */
  getCentreName(): string {
    if (this.selectedCentres.length === 0) return 'All Centres';
    return this.selectedCentres
      .map(code => this.loadedCentres.find(c => c.centreCode === code)?.centreName || code)
      .join(', ');
  }

  // Toggle centre list expansion in teachers table
  toggleCentre(row: Teacher): void {
    row.expanded = !row.expanded;
  }

  /** Open the manage learning concepts dialog */
  openManageLearningConcepts(): void {
    import('./manage-learning-concepts-dialog/manage-learning-concepts-dialog.component').then(m => {
      this.dialog.open(m.ManageLearningConceptsDialogComponent, {
        width: '560px',
        disableClose: false
      });
      // learningConceptOptions auto-refreshes via the existing RT DB subscription
    });
  }

  /** Reset highlights */
  clearHighlights(): void {
    this.highlightsDataSource.data = [];
  }

  // Component visibility methods
  toggleComponent(componentId: string): void {
    const component = this.components.find(c => c.id === componentId);
    if (component) {
      component.visible = !component.visible;
      this.updateAllSelectedState();
    }
  }

  toggleAll(): void {
    const newState = !this.allSelected;   // allSelected is a getter — always reflects real state
    this.components.forEach(c => c.visible = newState);
  }

  updateAllSelectedState(): void {
    // No-op: allSelected is now a computed getter derived from components[], so it never drifts.
  }

  isComponentVisible(componentId: string): boolean {
    const component = this.components.find(c => c.id === componentId);
    return component ? component.visible : false;
  }

  navigateToList(): void {
    this.router.navigate(['/home/reports/education-presentation-list']);
  }

  /** Switch from view-only mode to edit mode without reloading */
  switchToEditMode(): void {
    this.isEditMode = true;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id: this.currentPresentationId, mode: 'edit' },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.snackBar.open('Switched to Edit mode', 'Close', { duration: 2000 });
  }

  /**
   * Open photo gallery dialog for selecting images
   */
  openPhotoGallery(): void {
    if (!this.mStartDate || !this.mEndDate) {
      alert('Please select a date range first');
      return;
    }

    import('./education-photo-gallery/education-photo-gallery.component').then(m => {
      const dialogRef = this.dialog.open(m.EducationPhotoGalleryComponent, {
        width: '90vw',
        maxWidth: '1400px',
        height: '90vh',
        data: {
          // pass first selected centre or empty string (gallery handles empty = all)
          centreCode: this.selectedCentres[0] || '',
          centreCodes: this.selectedCentres,
          startDate: this.mStartDate,
          endDate: this.mEndDate
        }
      });

      dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(result => {
        if (result && Array.isArray(result)) {
          this.galleryImages = result;
          this.snackBar.open(`${this.galleryImages.length} image(s) added to gallery`, 'Close', { duration: 3000 });
        }
      });
    });
  }

  /**
   * Remove image from gallery
   */
  removeImageFromGallery(index: number): void {
    if (index >= 0 && index < this.galleryImages.length) {
      this.galleryImages.splice(index, 1);
      this.snackBar.open('Image removed from gallery', 'Close', { duration: 2000 });
    }
  }

  /**
   * Clear all images from gallery
   */
  clearGallery(): void {
    this.galleryImages = [];
    this.snackBar.open('Gallery cleared', 'Close', { duration: 2000 });
  }

  /**
   * Open image in new tab
   */
  openImageInNewTab(url: string): void {
    window.open(url, '_blank');
  }

}
